module Meevo.Plugins.OnlinePresenceSettings {
    "use strict";

    import MaintenanceFormState = Meevo.Model.MaintenanceFormState;

    const HAIR_SALON_ID = 2113;

    interface IOnlinePresenceSettingsModel {
        selectedPrimaryCategory: number;
        isLocationSuccessfullyOnboarded: boolean;
        lastSyncDate: string;
        isServicesAdded: boolean;
    }

    export interface ICategoryObject {
        id: string;
        parentId: string;
        name: string;
        fullName: string;
        selectable: boolean;
        entityTypeAvailability: {
            mode: string;
            entityTypes: string[];
        };
        countryAvailability: {
            mode: string;
            countries: any[];
        };
    }

    export interface ICategory {
        id: string;
        name: string;
    }

    export class MainSection extends Meevo.Components.CommonClasses.CommonSectionBase {
        protected busyService: Meevo.Components.Busy.IBusyService;
        protected locationId = this.identityService.locationId;

        public categoriesList: ICategory[] = [];

        public isPresenceManagementInLoading: boolean;
        public isPresenceManagementInSync: boolean;

        constructor(protected params: Meevo.Model.ISectionParamsCommonDataService,
                    protected modalService: Meevo.Components.Modal.IModalService,
                    protected $rootScope: ng.IRootScopeService,
                    protected appSettings: Meevo.Components.Common.IAppSettings,
                    protected $translate: angular.translate.ITranslateService,
                    protected $q: angular.IQService,
                    protected context: OnlinePresenceSettingsContext) {
            super(params.sectionId,
                params.displayNameContentKey,
                params.template,
                params.isPrimarySection,
                params.handlesOwnCrud,
                params.baseEndpoint,
                params.sectionEndpoint,
                params.loggingService,
                params.dataService,
                params.noun,
                params.identityService,
                params.sectionSecurityKey
            );

            this.busyService = params.busyService;
        }

        public activate(): void {
            super.activate();

            this.getYextCategories();
        }

        // Get list of categories from Yext
        public getYextCategories(): ng.IPromise<any> {
            this.busyService.start();

            this.model.selectedPrimaryCategory = this.model.selectedPrimaryCategory ? this.model.selectedPrimaryCategory : HAIR_SALON_ID;
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/Yext/Categories`, 'GET', null).then((res) => {
                const allCategories = res.data.response as ICategoryObject[];
                let filteredCategories = allCategories.filter(category => category.selectable === true),
                    beautyCategories = filteredCategories.filter(category => category.fullName.includes('Beauty & Spas')),
                    otherCategories = filteredCategories.filter(category => !category.fullName.includes('Beauty & Spas')),
                    orderedCategories = beautyCategories.concat(otherCategories);

                this.isPresenceManagementInLoading = this.model.isServicesAdded;

                orderedCategories.forEach((category: any) => {
                    let item: ICategory = {
                        id: category.id,
                        name: category.fullName
                    }

                    this.categoriesList.push(item);
                });

                this.busyService.stop();
            });
        }

        // Sync business info
        public syncBusinessInfo(): void {
            this.busyService.start();

            this.dataService.customMethodData(`${this.appSettings.API_ROOT}/tenant/businessinfo/`, 'GET', null).then((res) => {
                    let businessInfo = res.data,
                        selectedCategory = this.categoriesList.find(category => parseInt(category.id) === parseInt(this.model.selectedPrimaryCategory));

                    this.busyService.stop();

                    // Initialize modal business info
                    const options: Components.Modal.IModalOptions = {
                        templateUrl: 'app/plugins/onlinePresenceSettings/businessInfoModal/businessInfoModal.template.html',
                        clickOutsideToClose: true,
                        locals: {
                            businessInfo: businessInfo,
                            primaryCategory: selectedCategory.name
                        },
                        controller: 'meevo.plugins.onlinePresenceSettings.BusinessInfoModalController',
                        controllerAs: 'vm'
                    };
                    this.modalService.showCustom(options)
                        .then((response: any) => {
                            if (response) {
                                this.busyService.start();
                                
                                this.dataService.customMethodData(`${this.appSettings.API_ROOT}/Yext/Settings/Sync`, 'POST', null).then((info) => {
                                    if (info.data.lastSyncDate) {
                                        this.modalService.info('MESSAGE_SUCCESSFULLY_SYNCED').then(() => {
                                            this.model.lastSyncDate = info.data.lastSyncDate;
                                            this.model.isLocationSuccessfullyOnboarded = true;
                                        });
                                    }

                                    this.busyService.stop();
                                });
                            }
                    });
                });
        }

        // Copied from exception.interceptor.ts
        private formatValidationError(responseStatus): string {
            let message = null;

            if (responseStatus.errors && responseStatus.errors.length > 0) {
                if (responseStatus.errors.length === 1) {
                    let error = responseStatus.errors[0];
                    message = this.$translate.instant(error.message, error.customState);
                } else {
                    let messages = ['<ul style="margin:0">'];
                    for (let error of responseStatus.errors) {
                        messages.push("<li>", this.$translate.instant(error.message, error.customState), "</li>");
                    }
                    messages.push('</ul>');
                    message = messages.join('');
                }
            }
            else if (responseStatus.message) {
                message = this.$translate.instant(responseStatus.message);
            }

            return message;
        }

        public checkEnable(isServicesAdded: boolean) {
            if(isServicesAdded) {
                this.modalService.info('MESSAGE_SWITCHED_ENABLE_ONLINE_PRESENCE_MANAGEMENT')
            }
        }

        dataSave(): angular.IPromise<any> {
            if (this.model.isServicesAdded && !this.model.selectedPrimaryCategory) {
                this.modalService.info('MESSAGE_SELECT_PRIMARY_BEFORE_SAVING');

                return this.$q.resolve(null);
            }

            if (!this.model.isServicesAdded && this.model.selectedPrimaryCategory && ((!this.isPresenceManagementInLoading && !this.isPresenceManagementInSync))) {
                this.context.setMode(MaintenanceFormState.Readonly);
                return;
            }

            if (!this.model.isServicesAdded && this.model.selectedPrimaryCategory && (this.isPresenceManagementInSync || this.isPresenceManagementInLoading)) {
                return this.modalService
                    .redAlertConfirm('MESSAGE_CONFIRM_ONLINE_PRESENCE_MANAGEMENT_BEFORE_SAVING')
                    .then(() => {
                        return this.syncYextLocation();
                    })
                    .catch(() => {
                        this.model.isServicesAdded = true;
                    })
            } else {
                return this.syncYextLocation();
            }
        }

        syncYextLocation(): ng.IPromise<void> {
            let criteria = {
                categoryIds: [
                    this.model.selectedPrimaryCategory
                ],
                isServicesAdded: this.model.isServicesAdded
            };

            // don`t do this at home! but we need to start busyService after the maintenanceForm it closes
            setTimeout(() => {
                this.$rootScope.$apply(() => {
                    this.busyService.start();
                });
            });

            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/Yext/Settings`, 'POST', criteria)
                .then((res) => {
                    this.model = angular.copy(res.data);
                    this.isPresenceManagementInSync = this.model.isServicesAdded;
                    this.context.setMode(MaintenanceFormState.Readonly);
                })
                .finally(() => this.busyService.stop());
        }

        get isReadonly(): boolean {
            return this.mode === MaintenanceFormState.Readonly;
        }
    }
}
